import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import { InvisiblyApiResponse } from 'src/services/api/api-response.types';
import { sortGrossMarginRowsRecentFirst } from '../utils/sort-gross-margin-rows';
import type { CommissionInvoice, CommissionInvoiceLine, CommissionReport, GrossMarginRow, PayoutUser, Referral, ReferralLine, ReferralLineInvoiceLine } from '../types/commissions.types';

/** POST b2b/commissions/invoices/sync can run for a long time server-side */
const COMMISSION_INVOICES_SYNC_TIMEOUT_MS = 900_000;

/** QuickBooks-style line item inside details.Line (PascalCase from API; we also read snake_case) */
interface InvoiceDetailsLine {
  Id?: string | null;
  Amount?: number;
  LineNum?: number;
  Description?: string | null;
  DetailType?: string;
  SalesItemLineDetail?: {
    ItemRef?: { name?: string; value?: string };
  };
}

/** QuickBooks-style details object on the invoice (for drawer line items and overrides) */
interface InvoiceDetails {
  Id?: string;
  DocNumber?: string;
  CustomerRef?: { name?: string };
  DueDate?: string;
  TxnDate?: string;
  TotalAmt?: number;
  Line?: InvoiceDetailsLine[];
  MetaData?: { CreateTime?: string };
  LinkedTxn?: Array<{ TxnType?: string }>;
}

/** Raw row may have details or invoice_details; details may have Line or line */
function getDetailsFromRow(row: Record<string, unknown>): InvoiceDetails | null {
  const details = row.details ?? row.invoice_details;
  if (details != null && typeof details === 'object') return details as InvoiceDetails;
  if (Array.isArray(row.Line) || Array.isArray(row.line)) return row as unknown as InvoiceDetails;
  return null;
}

/** New commissions API invoice row (snake_case) */
interface CommissionInvoiceApiRowNew {
  id?: string;
  created_at?: string;
  updated_at?: string;
  status?: string | null;
  ext_invoice_id?: string;
  ext_customer_id?: string;
  customer_name?: string;
  amount?: number;
  invoice_date?: string;
  due_date?: string;
  paid_date?: string | null;
  is_paid?: boolean;
  month?: number;
  year?: number;
  include_in_total?: boolean;
  details?: InvoiceDetails;
}

/** QuickBooks-style invoice row from commissions invoices API */
interface CommissionInvoiceApiRowLegacy {
  Id?: string;
  DocNumber?: string;
  CustomerRef?: { name?: string };
  TotalAmt?: number;
  TxnDate?: string;
  DueDate?: string;
  LinkedTxn?: Array<{ TxnType?: string }>;
  MetaData?: { CreateTime?: string };
  id?: string;
  invoice_number?: string;
  invoiceNumber?: string;
  company_name?: string;
  companyName?: string;
  amount?: number;
  month?: number;
  year?: number;
  quarter?: number;
  status?: string;
  referral_id?: string;
  referralId?: string;
  is_initial?: boolean;
  isInitial?: boolean;
  due_date?: string;
  dueDate?: string;
  created_at?: string;
  createdAt?: string;
}

type CommissionInvoiceApiRow = CommissionInvoiceApiRowNew & CommissionInvoiceApiRowLegacy;

function parseMonthYear(txnDate: string | undefined): { month: number; year: number } {
  if (!txnDate) return { month: 0, year: 0 };
  const d = new Date(txnDate);
  if (Number.isNaN(d.getTime())) return { month: 0, year: 0 };
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

function monthToQuarter(month: number): number {
  if (month <= 0) return 0;
  return Math.ceil(month / 3);
}

function hasPaymentFromDetails(details: InvoiceDetails): boolean {
  const linked = details.LinkedTxn;
  return Array.isArray(linked) && linked.some((t) => t.TxnType === 'Payment');
}

/** Map details.Line (or details.line) to CommissionInvoiceLine[] (skip SubTotalLineDetail) */
function mapDetailsLinesToCommissionLines(details: InvoiceDetails | Record<string, unknown>): CommissionInvoiceLine[] {
  const d = details as Record<string, unknown>;
  const lineArr = (d.Line ?? d.line) as unknown[] | undefined;
  if (!Array.isArray(lineArr)) return [];
  return lineArr
    .filter((line) => {
      const L = line as Record<string, unknown>;
      const detailType = (L.DetailType ?? L.detail_type) as string | undefined;
      return detailType !== 'SubTotalLineDetail' && detailType !== 'SubtotalLineDetail';
    })
    .map((line, index) => {
      const L = line as Record<string, unknown>;
      const salesDetail = (L.SalesItemLineDetail ?? L.sales_item_line_detail) as Record<string, unknown> | undefined;
      const itemRef = salesDetail?.ItemRef ?? salesDetail?.item_ref;
      const ref = itemRef as { name?: string; value?: string } | undefined;
      const skuName = ref?.name != null ? String(ref.name) : undefined;
      return {
        id: String(L.Id ?? L.id ?? `line-${index}`),
        sku: String(ref?.value ?? ref?.name ?? '—'),
        ...(skuName && { skuName }),
        amount: Number(L.Amount ?? L.amount ?? 0),
        name: String(L.Description ?? L.description ?? ref?.name ?? '—'),
      };
    });
}

/** Normalize API row (new shape or QuickBooks legacy) to CommissionInvoice */
function toCommissionInvoice(row: CommissionInvoiceApiRow | Record<string, unknown>): CommissionInvoice {
  const r = row as CommissionInvoiceApiRow;

  const id = String(r.id ?? r.Id ?? '');
  const invoiceNumber = String(
    r.ext_invoice_id ?? r.DocNumber ?? r.invoice_number ?? r.invoiceNumber ?? ''
  );
  const companyName = String(
    r.customer_name ?? r.CustomerRef?.name ?? r.company_name ?? r.companyName ?? ''
  );
  const extCustomerId = (r as CommissionInvoiceApiRowNew).ext_customer_id != null
    ? String((r as CommissionInvoiceApiRowNew).ext_customer_id)
    : undefined;
  const amount = Number(r.amount ?? r.TotalAmt ?? 0);
  const dueDate = String(r.due_date ?? r.DueDate ?? r.dueDate ?? '');
  const parsed = parseMonthYear(
    r.invoice_date ?? (r.TxnDate as string) ?? r.created_at ?? r.createdAt
  );
  const month =
    Number(r.month ?? 0) || parsed.month || 0;
  const year =
    Number(r.year ?? 0) || parsed.year || 0;
  const quarter = Number((r as CommissionInvoiceApiRowLegacy).quarter ?? 0) || monthToQuarter(month);

  let status: 'paid' | 'unpaid';
  if (typeof (r as CommissionInvoiceApiRowNew).is_paid === 'boolean') {
    status = (r as CommissionInvoiceApiRowNew).is_paid ? 'paid' : 'unpaid';
  } else if (r.status === 'paid' || r.status === 'unpaid') {
    status = r.status;
  } else {
    status = hasPayment(r as CommissionInvoiceApiRowLegacy) ? 'paid' : 'unpaid';
  }

  const createdAt = String(
    r.created_at ?? r.createdAt ?? (r as CommissionInvoiceApiRowLegacy).MetaData?.CreateTime ?? ''
  );
  const paidDate = (r as CommissionInvoiceApiRowNew).paid_date != null
    ? String((r as CommissionInvoiceApiRowNew).paid_date)
    : undefined;

  const rowRecord = r as Record<string, unknown>;
  const details = getDetailsFromRow(rowRecord);
  let finalInvoiceNumber = invoiceNumber;
  let finalCompanyName = companyName;
  let finalDueDate = dueDate;
  let finalCreatedAt = createdAt;
  let finalStatus = status;
  let lines: CommissionInvoiceLine[] | undefined;

  if (details != null) {
    const d = details as Record<string, unknown>;
    if (d.DocNumber != null) finalInvoiceNumber = String(d.DocNumber);
    const customerRef = d.CustomerRef as { name?: string } | undefined;
    if (customerRef?.name != null) finalCompanyName = String(customerRef.name);
    if (d.DueDate != null) finalDueDate = String(d.DueDate);
    const meta = d.MetaData as { CreateTime?: string } | undefined;
    if (meta?.CreateTime != null) finalCreatedAt = String(meta.CreateTime);
    if (hasPaymentFromDetails(details as InvoiceDetails)) finalStatus = 'paid';
    lines = mapDetailsLinesToCommissionLines(details as Record<string, unknown>);
  }

  const includeInTotal = (r as CommissionInvoiceApiRowNew).include_in_total ?? true;

  return {
    id,
    invoiceNumber: finalInvoiceNumber,
    companyName: finalCompanyName,
    ...(extCustomerId && { extCustomerId }),
    amount,
    month,
    year,
    quarter,
    status: finalStatus,
    referralId: (r as CommissionInvoiceApiRowLegacy).referral_id != null
      ? String((r as CommissionInvoiceApiRowLegacy).referral_id)
      : (r as CommissionInvoiceApiRowLegacy).referralId != null
        ? String((r as CommissionInvoiceApiRowLegacy).referralId)
        : null,
    isInitial: Boolean((r as CommissionInvoiceApiRowLegacy).is_initial ?? (r as CommissionInvoiceApiRowLegacy).isInitial ?? false),
    dueDate: finalDueDate,
    createdAt: finalCreatedAt,
    includeInTotal,
    ...(paidDate && { paidDate }),
    ...(lines && lines.length > 0 && { lines }),
  };
}

function hasPayment(r: CommissionInvoiceApiRowLegacy): boolean {
  const linked = r.LinkedTxn;
  return Array.isArray(linked) && linked.some((t) => t.TxnType === 'Payment');
}

function roundTo2(value: number): number {
  return Math.round(value * 100) / 100;
}

function mapApiLineItemToReferralLineInvoiceLine(item: Record<string, unknown>): ReferralLineInvoiceLine {
  const invoiceId = item.invoice_id ?? item.invoiceId;
  return {
    id: String(item.id ?? ''),
    name: String(item.name ?? ''),
    rate: Number(item.rate ?? 0),
    ...(invoiceId != null && invoiceId !== '' && { invoiceId: String(invoiceId) }),
  };
}

/** Map API referral line (id, rate, recurring, customer_name, ext_customer_id, referral, lines?) to ReferralLine */
function mapApiReportToCommissionReport(r: Record<string, unknown>): CommissionReport {
  const type = String(r.type ?? '');
  const month = Number(r.month ?? 0);
  const year = Number(r.year ?? 0);
  const commission = Number(r.commission ?? 0);
  const referralId = r.referral ?? r.referral_id ?? r.referralId;
  const userId = r.user ?? r.user_id ?? r.userId;
  return {
    ...(r.id != null && { id: String(r.id) }),
    type,
    month,
    year,
    commission,
    ...(r.details != null && { details: r.details }),
    ...(referralId != null && referralId !== '' && { referral: String(referralId), referralId: String(referralId) }),
    ...(userId != null && userId !== '' && { user: String(userId), userId: String(userId) }),
  };
}

/** Map API referral line (id, rate, recurring, customer_name, ext_customer_id, referral, lines?) to ReferralLine */
function mapApiLineToReferralLine(ln: Record<string, unknown>): ReferralLine {
  const id = ln.id != null ? String(ln.id) : undefined;
  const rate = Number(ln.rate ?? 0);
  const recurring = ln.recurring ?? ln.isRecurring;
  const isRecurring = Boolean(recurring);
  const extCustomerId = String(ln.ext_customer_id ?? ln.extCustomerId ?? ln.companyId ?? '');
  const customerName = String(ln.customer_name ?? ln.customerName ?? '');
  const rawLineItems = ln.lines;
  const lineItems: ReferralLineInvoiceLine[] = Array.isArray(rawLineItems)
    ? rawLineItems.map((row) => mapApiLineItemToReferralLineInvoiceLine((row ?? {}) as Record<string, unknown>))
    : [];
  const monthRaw = ln.month ?? ln.period_month;
  const yearRaw = ln.year ?? ln.period_year;
  const month = monthRaw != null ? Number(monthRaw) : undefined;
  const year = yearRaw != null ? Number(yearRaw) : undefined;
  return {
    ...(id && { id }),
    rate,
    isRecurring,
    companyId: extCustomerId || undefined,
    customerName: customerName || undefined,
    ...(month != null && !Number.isNaN(month) && { month }),
    ...(year != null && !Number.isNaN(year) && { year }),
    ...(lineItems.length > 0 && { lines: lineItems }),
  };
}

function mapApiRowToReferral(r: Record<string, unknown>): Referral {
  const id = String(r.id ?? r.Id ?? '');
  // API shape: { id, created_at, updated_at, status, name, email, lines? } (support camelCase, PascalCase, snake_case)
  const name = String(
    r.name ?? r.Name ?? r.organization_name ?? r.organizationName ?? r.OrganizationName
      ?? r.referral_name ?? r.display_name ?? ''
  );
  const email = String(
    r.email ?? r.Email ?? r.contact_email ?? r.contactEmail ?? r.ContactEmail
      ?? r.referral_email ?? ''
  );
  const organizationName = name;
  const contactEmail = email;
  const initialRatePercent = Number(r.initial_rate_percent ?? r.initialRatePercent ?? 0);
  const recurringRatePercent = Number(r.recurring_rate_percent ?? r.recurringRatePercent ?? 0);
  const initialRateByMonth = (r.initial_rate_by_month ?? r.initialRateByMonth) as Record<string, number> | undefined;
  const recurringRateByMonth = (r.recurring_rate_by_month ?? r.recurringRateByMonth) as Record<string, number> | undefined;
  const estimatedPayoutForQuarter = r.estimated_payout_for_quarter != null || r.estimatedPayoutForQuarter != null
    ? Number(r.estimated_payout_for_quarter ?? r.estimatedPayoutForQuarter)
    : undefined;
  const rawLines = r.lines ?? r.referral_lines ?? r.referralLines;
  const referralLines: ReferralLine[] = Array.isArray(rawLines)
    ? rawLines.map((row) => mapApiLineToReferralLine((row ?? {}) as Record<string, unknown>))
    : [];
  return {
    id,
    name,
    email,
    organizationName,
    contactEmail,
    initialRatePercent,
    recurringRatePercent,
    initialRateByMonth: initialRateByMonth && typeof initialRateByMonth === 'object' ? initialRateByMonth : {},
    recurringRateByMonth: recurringRateByMonth && typeof recurringRateByMonth === 'object' ? recurringRateByMonth : {},
    ...(estimatedPayoutForQuarter != null && !Number.isNaN(estimatedPayoutForQuarter) && { estimatedPayoutForQuarter }),
    ...(referralLines.length > 0 && { referralLines, lines: referralLines }),
  };
}

function mapApiRowToPayoutUser(r: Record<string, unknown>): PayoutUser {
  const id = String(r.id ?? r.Id ?? '');
  const name = String(r.name ?? r.Name ?? '');
  const email = String(r.email ?? r.Email ?? '');
  const activePercent = Number(r.rate ?? r.active_percent ?? r.activePercent ?? 0);
  const percentByMonthRaw = r.percent_by_month ?? r.percentByMonth;
  const percentByMonth: Record<string, number> =
    percentByMonthRaw != null && typeof percentByMonthRaw === 'object' && !Array.isArray(percentByMonthRaw)
      ? (percentByMonthRaw as Record<string, number>)
      : {};
  const estimatedPayoutForQuarter =
    r.estimated_payout_for_quarter != null || r.estimatedPayoutForQuarter != null
      ? Number(r.estimated_payout_for_quarter ?? r.estimatedPayoutForQuarter)
      : undefined;
  return {
    id,
    name,
    email,
    activePercent,
    percentByMonth,
    ...(estimatedPayoutForQuarter != null && !Number.isNaN(estimatedPayoutForQuarter) && { estimatedPayoutForQuarter }),
  };
}

function mapPnlRowToGrossMarginRow(r: Record<string, unknown>): GrossMarginRow {
  const cogsVal = r.total_cogs ?? r.cogs ?? r.COGS;
  const totalIncome = Number(r.total_income ?? r.totalIncome ?? 0);
  const cogs = cogsVal == null ? null : Number(cogsVal);
  const activeTotalIncomeRaw = r.active_total_income ?? r.activeTotalIncome;
  const activeTotalIncome =
    activeTotalIncomeRaw != null ? roundTo2(Number(activeTotalIncomeRaw)) : undefined;
  const id = r.id != null ? String(r.id) : undefined;
  return {
    ...(id && { id }),
    month: Number(r.month ?? 0),
    year: Number(r.year ?? 0),
    totalIncome: roundTo2(totalIncome),
    cogs: cogs == null ? null : roundTo2(cogs),
    ...(activeTotalIncome !== undefined && { activeTotalIncome }),
  };
}

const commissionsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getCommissionInvoices: builder.query<CommissionInvoice[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/commissions/invoices',
      }),
      providesTags: [{ type: APITagType.CommissionInvoices, id: 'LIST' }],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        let list: unknown[] = [];

        if (Array.isArray(response)) {
          list = response;
        } else if (Array.isArray(res.item)) {
          list = res.item;
        } else if (Array.isArray(res.items)) {
          list = res.items;
        } else if (Array.isArray(res.Invoice)) {
          list = res.Invoice;
        } else if (Array.isArray(res.invoices)) {
          list = res.invoices;
        } else if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.item != null && typeof res.item === 'object') {
          const item = res.item as Record<string, unknown>;
          if (Array.isArray(item.Invoice)) {
            list = item.Invoice;
          } else if (item.QueryResponse != null && typeof item.QueryResponse === 'object') {
            const qr = (item.QueryResponse as Record<string, unknown>).Invoice;
            list = Array.isArray(qr) ? qr : [];
          } else if (item.DocNumber != null || item.Id != null) {
            list = [res.item];
          } else if (Array.isArray((item as { items?: unknown[] }).items)) {
            list = (item as { items: unknown[] }).items;
          } else if (Array.isArray((item as { invoices?: unknown[] }).invoices)) {
            list = (item as { invoices: unknown[] }).invoices;
          }
        } else if (res.QueryResponse != null && typeof res.QueryResponse === 'object') {
          const qr = res.QueryResponse as Record<string, unknown>;
          const invoice = qr.Invoice;
          list = Array.isArray(invoice) ? invoice : [];
        } else if (res.DocNumber != null || res.Id != null) {
          list = [response];
        } else {
          for (const key of Object.keys(res)) {
            const val = res[key];
            if (Array.isArray(val) && val.length > 0) {
              const first = val[0] as Record<string, unknown> | null;
              if (first && typeof first === 'object' && (first.Id != null || first.DocNumber != null || first.id != null || first.ext_invoice_id != null || first.customer_name != null)) {
                list = val;
                break;
              }
            }
          }
        }

        return list.map((row) => toCommissionInvoice((row ?? {}) as Record<string, unknown>));
      },
    }),
    getCommissionInvoiceById: builder.query<CommissionInvoice, string>({
      query: (id) => ({
        method: 'get',
        url: `b2b/commissions/invoices/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: APITagType.CommissionInvoices, id }],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? response;
        return toCommissionInvoice((raw ?? {}) as Record<string, unknown>);
      },
    }),
    getCommissionReferrals: builder.query<Referral[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/commissions/referrals',
      }),
      providesTags: [APITagType.CommissionReferrals],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        let list: unknown[] = [];
        // API may return a top-level array: [{ id, created_at, updated_at, status, name, email }, ...]
        if (Array.isArray(response)) {
          list = response;
        } else if (Array.isArray(res.items)) {
          list = res.items;
        } else if (Array.isArray(res.data)) {
          list = res.data;
        } else if (Array.isArray(res.referrals)) {
          list = res.referrals;
        } else if (Array.isArray(res.item)) {
          list = res.item;
        } else if (res.item != null && typeof res.item === 'object' && !Array.isArray(res.item)) {
          const inner = res.item as Record<string, unknown>;
          if (Array.isArray(inner.referrals)) list = inner.referrals;
          else if (Array.isArray(inner.data)) list = inner.data;
          else if (Array.isArray(inner.items)) list = inner.items;
        }
        return list.map((row) => mapApiRowToReferral((row ?? {}) as Record<string, unknown>));
      },
    }),
    createCommissionReferral: builder.mutation<Referral, { name: string; email: string }>({
      query: ({ name, email }) => {
        const data = { name, email };
        return {
          method: 'post',
          url: 'b2b/commissions/referrals',
          data,
          body: data,
        };
      },
      invalidatesTags: [APITagType.CommissionReferrals],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiRowToReferral((raw ?? {}) as Record<string, unknown>);
      },
    }),
    updateCommissionReferral: builder.mutation<
    Referral,
    { id: string; name: string; email: string }
    >({
      query: ({ id, name, email }) => ({
        method: 'patch',
        url: `b2b/commissions/referrals/${id}`,
        data: { name, email },
        body: { name, email },
      }),
      invalidatesTags: [APITagType.CommissionReferrals],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiRowToReferral((raw ?? {}) as Record<string, unknown>);
      },
    }),
    createCommissionReferralLine: builder.mutation<
    ReferralLine,
    {
      referral: string;
      rate: number;
      recurring: boolean;
      customer_name: string;
      ext_customer_id: string;
      lines?: ReferralLineInvoiceLine[];
      month?: number;
      year?: number;
    }
    >({
      query: (body) => ({
        method: 'post',
        url: 'b2b/commissions/referral/lines',
        data: body,
        body: body,
      }),
      invalidatesTags: [APITagType.CommissionReferrals],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiLineToReferralLine((raw ?? {}) as Record<string, unknown>);
      },
    }),
    updateCommissionReferralLine: builder.mutation<
    ReferralLine,
    {
      id: string;
      rate: number;
      recurring: boolean;
      customer_name: string;
      ext_customer_id: string;
      lines?: ReferralLineInvoiceLine[];
      month?: number;
      year?: number;
    }
    >({
      query: ({ id, ...body }) => ({
        method: 'patch',
        url: `b2b/commissions/referral/lines/${id}`,
        data: body,
        body: body,
      }),
      invalidatesTags: [APITagType.CommissionReferrals],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiLineToReferralLine((raw ?? {}) as Record<string, unknown>);
      },
    }),
    deleteCommissionReferralLine: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        method: 'delete',
        url: `b2b/commissions/referral/lines/${id}`,
      }),
      invalidatesTags: [APITagType.CommissionReferrals],
    }),
    getCommissionReports: builder.query<
    CommissionReport[],
    { type?: string; month?: number; year?: number } | void
    >({
      query: (arg) => {
        const params: Record<string, string | number> = {};
        if (arg?.type != null) params.type = arg.type;
        if (arg?.month != null) params.month = arg.month;
        if (arg?.year != null) params.year = arg.year;
        return {
          method: 'get',
          url: 'b2b/commissions/reports',
          ...(Object.keys(params).length > 0 && { params }),
        };
      },
      providesTags: [APITagType.CommissionReports],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        let list: unknown[] = [];
        if (Array.isArray(response)) list = response;
        else if (Array.isArray(res.items)) list = res.items;
        else if (Array.isArray(res.data)) list = res.data;
        else if (Array.isArray(res.item)) list = res.item;
        return list.map((row) => mapApiReportToCommissionReport((row ?? {}) as Record<string, unknown>));
      },
    }),
    updateCommissionReport: builder.mutation<
    CommissionReport,
    { id: string; commission: number }
    >({
      query: ({ id, commission }) => ({
        method: 'patch',
        url: `b2b/commissions/reports/${id}`,
        body: { commission },
      }),
      invalidatesTags: [APITagType.CommissionReports],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiReportToCommissionReport((raw ?? {}) as Record<string, unknown>);
      },
    }),
    getCommissionUsers: builder.query<PayoutUser[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/commissions/users',
      }),
      providesTags: [APITagType.CommissionUsers],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        let list: unknown[] = [];
        if (Array.isArray(response)) {
          list = response;
        } else if (Array.isArray(res.items)) {
          list = res.items;
        } else if (Array.isArray(res.data)) {
          list = res.data;
        } else if (Array.isArray(res.item)) {
          list = res.item;
        } else if (Array.isArray(res.users)) {
          list = res.users;
        } else if (res.item != null && typeof res.item === 'object' && !Array.isArray(res.item)) {
          const inner = res.item as Record<string, unknown>;
          if (Array.isArray(inner.users)) list = inner.users;
          else if (Array.isArray(inner.data)) list = inner.data;
          else if (Array.isArray(inner.items)) list = inner.items;
        }
        return list.map((row) => mapApiRowToPayoutUser((row ?? {}) as Record<string, unknown>));
      },
    }),
    createCommissionUser: builder.mutation<
    PayoutUser,
    { name: string; email: string; rate: number }
    >({
      query: ({ name, email, rate }) => {
        const data = { name, email, rate };
        return {
          method: 'post',
          url: 'b2b/commissions/users',
          data,
          body: data,
        };
      },
      invalidatesTags: [APITagType.CommissionUsers],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiRowToPayoutUser((raw ?? {}) as Record<string, unknown>);
      },
    }),
    updateCommissionUser: builder.mutation<
    PayoutUser,
    { id: string; name?: string; email?: string; rate?: number }
    >({
      query: ({ id, name, email, rate }) => {
        const body: Record<string, unknown> = {};
        if (name !== undefined) body.name = name;
        if (email !== undefined) body.email = email;
        if (rate !== undefined) body.rate = rate;
        return {
          method: 'patch',
          url: `b2b/commissions/users/${id}`,
          data: body,
          body,
        };
      },
      invalidatesTags: [APITagType.CommissionUsers],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? res.data ?? response;
        return mapApiRowToPayoutUser((raw ?? {}) as Record<string, unknown>);
      },
    }),
    getCommissionPnl: builder.query<GrossMarginRow[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/commissions/pnl',
      }),
      providesTags: [APITagType.CommissionPnl],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        let list: unknown[] = [];
        if (Array.isArray(response)) {
          list = response;
        } else if (Array.isArray(res.items)) {
          list = res.items;
        } else if (Array.isArray(res.data)) {
          list = res.data;
        } else if (Array.isArray(res.item)) {
          list = res.item;
        }
        return sortGrossMarginRowsRecentFirst(
          list.map((row) => mapPnlRowToGrossMarginRow((row ?? {}) as Record<string, unknown>))
        );
      },
    }),
    createCommissionPnl: builder.mutation<
    GrossMarginRow,
    { month: number; year: number; totalIncome: number }
    >({
      query: ({ month, year, totalIncome }) => {
        const data = {
          month,
          year,
          total_income: totalIncome,
        };
        return {
          method: 'post',
          url: 'b2b/commissions/pnl',
          data,
          body: data,
        };
      },
      invalidatesTags: [APITagType.CommissionPnl],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? response;
        return mapPnlRowToGrossMarginRow((raw ?? {}) as Record<string, unknown>);
      },
    }),
    updateCommissionPnl: builder.mutation<
    GrossMarginRow,
    { id: string; totalIncome: number; cogs: number | null }
    >({
      query: ({ id, totalIncome, cogs }) => {
        const data = {
          total_income: totalIncome,
          total_cogs: cogs ?? 0,
        };
        return {
          method: 'patch',
          url: `b2b/commissions/pnl/${id}`,
          data,
          body: data,
        };
      },
      invalidatesTags: [APITagType.CommissionPnl],
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? response;
        return mapPnlRowToGrossMarginRow((raw ?? {}) as Record<string, unknown>);
      },
    }),
    syncCommissionInvoices: builder.mutation<void, void>({
      query: () => ({
        method: 'post',
        url: 'b2b/commissions/invoices/sync',
        timeout: COMMISSION_INVOICES_SYNC_TIMEOUT_MS,
      }),
      invalidatesTags: [APITagType.CommissionInvoices],
    }),
    updateCommissionInvoiceIncludeInTotal: builder.mutation<
    CommissionInvoice,
    { id: string; includeInTotal: boolean }
    >({
      query: ({ id, includeInTotal }) => ({
        method: 'patch',
        url: `b2b/commissions/invoices/${id}`,
        body: { include_in_total: includeInTotal },
      }),
      async onQueryStarted({ id, includeInTotal }, { dispatch, queryFulfilled }) {
        // Optimistic update: keep invoice in list with toggle off/on so it always shows
        const patchList = dispatch(
          invisiblyApi.util.updateQueryData('getCommissionInvoices', undefined, (draft) => {
            const inv = draft.find((i) => i.id === id);
            if (inv) inv.includeInTotal = includeInTotal;
          })
        );
        const patchDetail = dispatch(
          invisiblyApi.util.updateQueryData('getCommissionInvoiceById', id, (draft) => {
            draft.includeInTotal = includeInTotal;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchList.undo();
          patchDetail.undo();
        }
      },
      transformResponse(response: InvisiblyApiResponse<unknown>) {
        const res = response as Record<string, unknown>;
        const raw = res.item ?? response;
        return toCommissionInvoice((raw ?? {}) as Record<string, unknown>);
      },
    }),
  }),
});

export const {
  useGetCommissionInvoicesQuery,
  useGetCommissionInvoiceByIdQuery,
  useGetCommissionReferralsQuery,
  useGetCommissionReportsQuery,
  useUpdateCommissionReportMutation,
  useCreateCommissionReferralMutation,
  useUpdateCommissionReferralMutation,
  useCreateCommissionReferralLineMutation,
  useUpdateCommissionReferralLineMutation,
  useDeleteCommissionReferralLineMutation,
  useGetCommissionUsersQuery,
  useCreateCommissionUserMutation,
  useUpdateCommissionUserMutation,
  useGetCommissionPnlQuery,
  useCreateCommissionPnlMutation,
  useUpdateCommissionPnlMutation,
  useUpdateCommissionInvoiceIncludeInTotalMutation,
  useSyncCommissionInvoicesMutation,
} = commissionsEndpoints;
